import { jwtDecode } from "jwt-decode";
import { ParsedQs } from "qs";

export function parseQueryParam(
  param: string | ParsedQs | string[] | ParsedQs[] | undefined
): string {
  if (typeof param === "string") {
    return param;
  }

  if (Array.isArray(param)) {
    return param.join("; ");
  }

  return "";
}

export function parseJWT(token: string) {
  return jwtDecode(token);
}

/**
 * User info for SSO detection. Supports both:
 * - Unlinked SAML users: sub starts with "samlp|"
 * - Linked SSO users: Have auth0| sub but custom claims from Auth0 Action
 */
export interface SsoUserInfo {
  sub?: string;
  "https://certn.co/sso_only"?: boolean;
  "https://certn.co/sso_connection"?: string;
}

/**
 * Checks if a user authenticated via SAML SSO.
 *
 * Handles two scenarios:
 * 1. Unlinked SAML users: sub starts with "samlp|"
 * 2. Linked SSO users: Have an auth0| sub but sso_only claim is true
 *    (set by the automatic-account-linking Auth0 Action)
 *
 * @param user - The user object from Auth0, or just the sub string for backward compatibility
 * @returns true if the user authenticated via SAML SSO
 */
export function isSsoUser(user: SsoUserInfo | string | undefined): boolean {
  if (!user) {
    return false;
  }

  // Handle string input (backward compatibility)
  if (typeof user === "string") {
    return user.startsWith("samlp|");
  }

  // Linked SSO users have this claim set by Auth0 Action
  if (user["https://certn.co/sso_only"] === true) {
    return true;
  }

  // Unlinked SAML users (edge case - before linking occurs)
  return user.sub?.startsWith("samlp|") ?? false;
}

/**
 * Extracts the connection name from an SSO user.
 *
 * Handles two scenarios:
 * 1. Unlinked SAML users: Parse from sub format "samlp|connection-name|user-id"
 * 2. Linked SSO users: Use the sso_connection claim set by Auth0 Action
 *
 * @param user - The user object from Auth0, or just the sub string for backward compatibility
 * @returns The connection name for SSO users, or null for non-SSO users
 * @example
 * extractConnectionName({ sub: "auth0|123", "https://certn.co/sso_connection": "acme-corp" }) // "acme-corp"
 * extractConnectionName({ sub: "samlp|acme-corp|user123" }) // "acme-corp"
 * extractConnectionName({ sub: "auth0|507f1f77bcf86cd799439011" }) // null
 */
export function extractConnectionName(
  user: SsoUserInfo | string | undefined
): string | null {
  if (!user) {
    return null;
  }

  // Handle string input (backward compatibility)
  if (typeof user === "string") {
    if (user.startsWith("samlp|")) {
      const parts = user.split("|");
      return parts[1];
    }
    return null;
  }

  // Linked users - use claim set by Auth0 Action
  if (user["https://certn.co/sso_connection"]) {
    return user["https://certn.co/sso_connection"];
  }

  // Unlinked SAML users - parse from sub
  if (user.sub?.startsWith("samlp|")) {
    const parts = user.sub.split("|");
    return parts[1];
  }

  return null;
}
